import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import {
  Button,
  DateRangePicker,
  Dialog,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Select,
  SnackBar,
  Typography,
} from 'components/atoms';
import { severityType } from 'components/atoms/Alert/Alert.styles';
import { DateRangePickerValue } from 'components/atoms/DateRangePicker/DateRangePicker';
import { Table } from 'components/organisms';
import {
  COMMON_OPTIONS,
  DATE_FORMAT,
  DATE_FORMAT_BACK_WITH_OUT_DATE,
  DATE_FORMAT_BACK_WITH_OUT_TIME,
  GET_PROCESS,
  GET_STATUS_BY_PROCESS,
  RE_PROCESS_TRANSACTION,
} from 'const';
import { useCustomerContext } from 'contexts';
import { format } from 'date-fns';
import { useDialog, useGet, useGetTable } from 'hooks';
import useTable, { filterModelType, tableModelType } from 'hooks/useTable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AiOutlineUpload } from 'react-icons/ai';
import { PaginationType, Process, State, Transaction } from 'types';
import { createFilterStructure, joinObjectWithFormat } from 'utils';

import { Auth } from 'types';
import { loadState, saveState } from 'utils';
import {
  defaultPagination,
  defaultSortModel,
  fetch,
  getColumns,
  getDefaultProcessTemplateFilter,
} from './ProcessTemplate.const';
import {
  ProcessTemplateFilterSchema,
  ProcessTemplateFilterType,
} from './ProcessTemplate.schema';
import { ProcessTemplateProps, responseType } from './ProcessTemplate.types';
import ProcessUploadTemplate from './ProcessUploadTemplate/ProcessUploadTemplate';

const ProcessTemplate = <T extends object = {}>({
  id,
}: ProcessTemplateProps) => {
  const { name } = useCustomerContext();
  const [loadingForm, setLoadingForm] = useState(false);

  const {
    loading: loadingProcesses,
    error: errorProcesses,
    data: process,
  } = useGet<Process>(GET_PROCESS.replace(':id', id));

  // Guardar el proceso en localStorage cuando esté disponible
  useEffect(() => {
    if (process) {
      saveState('currentProcess', process);
    }
  }, [process]);

  const {
    loading: loadingStates,
    error: errorStates,
    data: allStates,
  } = useGet<State[]>(GET_STATUS_BY_PROCESS.replace(':id', id));

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<ProcessTemplateFilterType>({
    defaultValues: getDefaultProcessTemplateFilter(id),
    resolver: yupResolver(ProcessTemplateFilterSchema),
  });

  const dateRange = watch('dateRange');

  const [filterModel, setFilterModel] = useState<
    filterModelType<Transaction<T>>[]
  >([
    createFilterStructure('id_process', 'equals', id) as filterModelType<
      Transaction<T>
    >,
    createFilterStructure(
      'createdAt',
      'between_date',
      joinObjectWithFormat(dateRange, '.', format, [
        DATE_FORMAT_BACK_WITH_OUT_TIME,
      ]),
    ) as filterModelType<Transaction<T>>,
    createFilterStructure(
      'createdAt',
      'between_time',
      joinObjectWithFormat(dateRange, '.', format, [
        DATE_FORMAT_BACK_WITH_OUT_DATE,
      ]),
    ) as filterModelType<Transaction<T>>,
  ]);

  const {
    loading: loadingTable,
    error: errorTransactions,
    data: processes,
    handleData,
    getData,
    mutate,
  } = useGetTable<
    PaginationType<Transaction<T>>,
    tableModelType<Transaction<T>>
  >(fetch, {
    paginationModel: defaultPagination,
    sortModel: defaultSortModel,
    filterModel,
  });

  const onReprocessed = async (id: string) => {
    try {
      setLoadingForm(true);
      const auth = loadState<Auth>('auth');
      const username: string = auth?.user?.username || '';
      await axios.put(RE_PROCESS_TRANSACTION.replace(':id', id.toString()).replace(':user', username));

      mutate(({ result, ...others }) => {
        const copyTransactions: Transaction<T>[] = [...result];
        const index = copyTransactions.findIndex(
          (transaction) => `${transaction.id}` === id,
        );
        if (index !== -1) {
          copyTransactions[index].reprocessed = false;
        }
        return { ...others, result: copyTransactions };
      });

      await handleData({
        paginationModel: tableControllers.paginationModel,
        sortModel: tableControllers.sortModel,
        filterModel,
      });
      onClose();
      setLoadingForm(false);
      setOpen('success');
    } catch (error) {
      onClose();
      setLoadingForm(false);
      setOpen('error');
    }
  };

  const [selected, onOpen, onCloseModal] = useDialog<number | undefined>(
    undefined,
  );
  const [open, setOpen] = useState<severityType>();
  const [action, setAction] = useState<'reprocess' | undefined>(undefined);
  const [waitingResponse, setWaitingResponse] = useState(false);
  const [actionSnackBar, setActionSnackBar] = useState<'reprocess' | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onCloseSnackBar = useCallback(() => {
    setOpen(undefined);
    setActionSnackBar(undefined);
  }, []);

  const onClose = useCallback(() => {
    onCloseModal();
    setAction(undefined);
  }, [onCloseModal]);

  const onUpload = useCallback(() => {
    onOpen(0);
    setAction('reprocess');
    setActionSnackBar('reprocess');
  }, [onOpen]);

  const dialogTitle = (): string => {
    if (action === "reprocess") {
      return "Carga excel para reprocesar paquetes";
    } else {
      return "Confirma esta acción";
    }
  };

  const confirmRemove = useCallback(
    (id: number) => {
      onOpen(id);
    },
    [onOpen],
  );

  const columns = useMemo(
    () => getColumns(name, confirmRemove, process?.canBeReprocessed),
    [name, process?.canBeReprocessed, confirmRemove],
  );

  const tableControllers = useTable<Transaction<T>>(
    {
      filterModel,
      paginationModel: processes
        ? { ...defaultPagination, rowsCount: processes.rowsCount }
        : defaultPagination,
    },
    handleData,
    getData as (
      param: Omit<tableModelType<Transaction<T>>, 'paginationModel'>,
    ) => Promise<responseType<T>>,
  );

  const onSubmit = useCallback(
    ({ package_, status, dateRange }: ProcessTemplateFilterType) => {
      const filterProcess = createFilterStructure('id_process', 'equals', id);

      const filterPackage = createFilterStructure(
        'idTransaction',
        'contains',
        package_,
      );

      const filterStatus = createFilterStructure('id', 'in', status, 'state');

      const filterDateRange = createFilterStructure(
        'createdAt',
        'between_date',
        joinObjectWithFormat(dateRange, '.', format, [
          DATE_FORMAT_BACK_WITH_OUT_TIME,
        ]),
      );

      const filterTimeRange = createFilterStructure(
        'createdAt',
        'between_time',
        joinObjectWithFormat(dateRange, '.', format, [
          DATE_FORMAT_BACK_WITH_OUT_DATE,
        ]),
      );

      const modelFilter = [
        filterPackage,
        filterStatus,
        filterDateRange,
        filterTimeRange,
        filterProcess,
      ].filter((item) => item);

      setFilterModel(modelFilter as filterModelType<Transaction<T>>[]);
    },
    [id],
  );

  const loading = loadingTable || loadingStates || loadingProcesses;
  const hasError = errorStates || errorTransactions || errorProcesses;

  const selectedRole = useMemo(
    () =>
      selected !== undefined
        ? processes?.result.find((user) => user.id === selected)
        : undefined,
    [selected, processes],
  );

  return (
    <>
      <Table<Transaction<T>>
        {...tableControllers}
        numberOfVisibleColumns={10}
        // title={`Encontramos ${tableControllers.paginationModel.rowsCount} resultados`}
        exportOptions={{
          filename: `reporte_${joinObjectWithFormat(dateRange, '-', format, [
            DATE_FORMAT,
          ])}`,
          headers: [...columns.filter(col => col.exportField).map(col => col.headerName), 'Reprocesar'],
          customExport: (data: Transaction<T>[]) => {
            // Solo añadir una columna vacía al final de cada fila
            return data.map(row => {
              const excelRow: any[] = [];
              columns
                .filter(col => col.exportField)
                .forEach(col => {
                  // Estado necesita manejo especial
                  if (col.field === 'id' && col.headerName === 'Estado') {
                    excelRow.push(row.status.name || '');
                  }
                  // Para otros campos, usar customExcelRender si existe
                  else if (col.customExcelRender) {
                    excelRow.push(col.customExcelRender(row));
                  }
                  // Para campos normales
                  else if (col.field === 'idTransaction') {
                    excelRow.push(row.idTransaction || '');
                  }
                  else if (col.field === 'createdAt') {
                    excelRow.push(format(new Date(row.createdAt), DATE_FORMAT, COMMON_OPTIONS));
                  }
                  else if (col.field === 'transactionDate') {
                    excelRow.push(format(new Date(row.updatedAt || row.createdAt), DATE_FORMAT, COMMON_OPTIONS));
                  }
                  else if (col.field === 'extraDataJson') {
                    const extraData = row.extraDataJson || {};
                    // Usar aserción de tipo para evitar errores de TS
                    const errorMessage = (extraData as any).errorMessage;
                    const detailMessage = (extraData as any).detailMessage;

                    if (row.status?.code?.toLowerCase().includes('error')) {
                      excelRow.push(errorMessage || detailMessage || '');
                    } else {
                      excelRow.push(detailMessage || '');
                    }
                  }
                  else {
                    excelRow.push((row as any)[col.field] || '');
                  }
                });

              // Añadir una celda vacía para la columna Reprocesar
              excelRow.push('');
              return excelRow;
            });
          }
        }}
        columns={columns}
        rows={processes?.result || []}
        error={!!hasError}
        loading={loading}
        filterComponent={
          <Grid
            container
            alignItems="center"
            justify="flex-end"
            spacing={4}
            wrap="nowrap"
          >
            <Grid item xs={12}>
              <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  alignItems="center"
                  justify="flex-end"
                  spacing={4}
                  wrap="nowrap"
                >
                  <Grid item xs={3}>
                    <Input
                      label="Filtra por paquete"
                      fullWidth
                      {...register('package_')}
                      error={!!errors.package_}
                      helperText={errors.package_ && errors.package_.message}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Select
                            value={field.value}
                            onChange={field.onChange}
                            label="Filtra por estado"
                            fullWidth
                            enableFilter
                            multiple
                          >
                            {allStates?.map(({ code, name, id }) => (
                              <MenuItem key={code} label={name} value={`${id}`}>
                                {name}
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={3}>
                <Controller
                  name="dateRange"
                  control={control}
                  render={({ field }) => (
                    <TimeRangePicker
                      label="Filtra por horas"
                      fullWidth
                      onChange={field.onChange}
                      value={field.value as TimeRangePickerValue}
                      helperText={
                        errors.dateRange &&
                        (errors.dateRange.startDate?.message ||
                          errors.dateRange.endDate?.message)
                      }
                    />
                  )}
                />
              </Grid> */}
                  <Grid item xs={3}>
                    <Controller
                      name="dateRange"
                      control={control}
                      render={({ field }) => (
                        <DateRangePicker
                          label="Filtra por fechas"
                          fullWidth
                          onChange={field.onChange}
                          value={field.value as DateRangePickerValue}
                          error={!!errors.dateRange}
                          helperText={
                            errors.dateRange &&
                            (errors.dateRange.startDate?.message ||
                              errors.dateRange.endDate?.message)
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item>
                    <Button type="submit" variant="contained" color="primary">
                      Filtrar
                    </Button>
                  </Grid>
                  <Grid item>
                    <IconButton
                      info="Cargar excel para reprocesar paquetes"
                      color="primary"
                      onClick={onUpload}
                    >
                      <AiOutlineUpload />
                    </IconButton>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        }
      />
      <Dialog
        open={selected !== undefined}
        onClose={onClose}
        title={dialogTitle()}
      >
        <Grid container spacing={0} justify="flex-end" style={{ width: '100%', margin: 0 }}>
          {action === 'reprocess' ? (
            <Grid item xs={12} style={{ padding: 0 }}>
              {!waitingResponse ? (
                <Grid container spacing={0}>
                  <Grid item xs={12} style={{ paddingBottom: '10px', paddingLeft: '10px' }}>
                    <Typography variant="p" style={{ fontSize: '14px' }}>
                      Carga un archivo Excel con la lista de paquetes a reprocesar
                    </Typography>
                  </Grid>
                  <Grid item xs={12} style={{ padding: 0 }}>
                    <ProcessUploadTemplate
                      waitingResponse={waitingResponse}
                      setWaitingResponse={setWaitingResponse}
                      onClose={onClose}
                      setOpen={setOpen}
                      setActionSnackBar={setActionSnackBar}
                      setErrorMessage={setErrorMessage}
                      processId={id}
                      process={process}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container justify="center" spacing={0} style={{ padding: '30px 0' }}>
                  <Grid item>
                    <Typography variant="p" style={{ fontSize: '14px' }}>Procesando archivo...</Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="h2">
                  {`¿Estas seguro de re-procesar la transacción ${selectedRole?.idTransaction}?`}
                </Typography>
              </Grid>
              <Grid item>
                <Button variant="contained" color="grey" onClick={onClose}>
                  Cancelar
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => onReprocessed(`${selectedRole?.id}`)}
                  loading={loadingForm}
                >
                  Si, deseo re-procesar
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Dialog>
      <SnackBar
        wait={actionSnackBar === 'reprocess' ? 10000 : 2000}
        open={open !== undefined}
        onClose={onCloseSnackBar}
        severity={open}
      >
        {open === 'success' && actionSnackBar === 'reprocess' &&
          'El archivo de reprocesamiento se cargó correctamente y los paquetes serán procesados en breve, este proceso puede tardar algunos minutos dependiendo del número de paquetes.'}
        {open === 'success' && actionSnackBar !== 'reprocess' &&
          'Se re-proceso la transacción exitosamente'}
        {open === 'error' && 'Ocurrió un error, intenta nuevamente'}
        {open === 'warning' && errorMessage}
      </SnackBar>
    </>
  );
};

export default ProcessTemplate;
